import { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { usePatchQueue } from "./usePatchQueue";
import { supabase } from "./supabaseClient";
import type { Prospect } from "./ProspectsContext";

type Draft = { text: string; version: number };

export function useProspectNote(prospect: Prospect) {
  const { user } = useAuth();
  const key = `prospect-call-note:${user?.id}:${prospect.id}`;
  const [initial] = useState<Draft>(() => {
    try {
      const draft = JSON.parse(sessionStorage.getItem(key) || "null");
      if (draft && typeof draft.text === "string" && Number.isInteger(draft.version)
        && draft.text !== prospect.call_note) return draft;
    } catch { /* Le stockage peut être indisponible. */ }
    return { text: prospect.call_note, version: prospect.call_note_version };
  });
  const [text, setText] = useState(initial.text);
  const currentText = useRef(initial.text);
  const version = useRef(initial.version);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState<Draft | null>(null);
  const remember = () => {
    try { sessionStorage.setItem(key, JSON.stringify({ text: currentText.current, version: version.current })); } catch { /* Sauvegarde distante toujours active. */ }
  };
  const queue = usePatchQueue<{ call_note: string }>(async patch => {
    const { data, error: saveError } = await supabase.rpc("save_prospect_call_note", {
      p_prospect_id: prospect.id, p_note: patch.call_note, p_expected_version: version.current,
    });
    if (saveError) {
      if (saveError.code === "40001") {
        setError("Une autre personne a modifié cette note. Votre texte est conservé.");
        const { data: latest } = await supabase.from("prospects").select("call_note,call_note_version").eq("id", prospect.id).single();
        if (latest) setConflict({ text: latest.call_note, version: latest.call_note_version });
      } else setError("La note n’a pas pu être enregistrée. Votre texte est conservé : réessayez.");
      throw saveError;
    }
    version.current = data;
    if (currentText.current === patch.call_note) {
      try { sessionStorage.removeItem(key); } catch { /* Rien à effacer. */ }
    } else remember();
    setError(""); setConflict(null);
  });
  useEffect(() => {
    if (initial.text !== prospect.call_note) queue.enqueue({ call_note: initial.text });
    // Restaurer le brouillon une seule fois, au montage de cette fiche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (queue.unsaved().call_note !== undefined || prospect.call_note_version < version.current) return;
    version.current = prospect.call_note_version;
    currentText.current = prospect.call_note;
    setText(prospect.call_note);
  }, [prospect.call_note, prospect.call_note_version, queue.unsaved]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (queue.unsaved().call_note !== undefined) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [queue.unsaved]);
  const change = (value: string) => {
    currentText.current = value; setText(value); remember(); queue.enqueue({ call_note: value });
  };
  const resolve = (merge: boolean) => {
    if (!conflict) return;
    version.current = conflict.version;
    change(merge ? [conflict.text, currentText.current].filter(Boolean).join("\n\n") : conflict.text);
    setConflict(null);
    void queue.flush();
  };
  return { text, change, flush: queue.flush, status: queue.status, error, conflict, resolve };
}
